import type { BaseFolderSecurity } from './group';
import type { GroupType, SortDirection } from './enums';
import {
  APP_TYPES,
  APP_REJECT_ASSIGN,
  APP_COMMON_TYPES_REJECT_DEVICE,
  APP_COMMON_TYPES_REJECT_SPLITSCREEN,
  COMMON_TYPE,
  PLAYLIST_IGNORE_APP_TYPES,
} from './app-types';

// --- Enums ---

export const ASSET_TYPES = {
  FILE: 'file',
  WEB: 'web',
  APP: 'app',
  SPLIT_SCREEN: 'splitScreen',
  FOLDER: 'folder',
  DATASOURCE: 'datasource',
  TEMPLATE: 'template',
  // OptiEdge MDM Phase 1 — backs Room Signage URLs; excluded from standard asset queries.
  HIDDEN: 'hidden',
} as const;

export type AssetType = (typeof ASSET_TYPES)[keyof typeof ASSET_TYPES];

export const ROOMS_SIGNAGE_VENDORS = {
  GOOGLE_MEET: 'google-meet',
  TEAMS_ROOMS: 'teams-rooms',
  ZOOM_ROOMS: 'zoom-rooms',
  WEBEX_ROOMS: 'webex-rooms',
} as const;

export type RoomsSignageVendor = (typeof ROOMS_SIGNAGE_VENDORS)[keyof typeof ROOMS_SIGNAGE_VENDORS];

export interface RoomsSignageUrlMeta {
  kind: 'rooms-signage-url';
  vendor: RoomsSignageVendor;
  target?: string;
  assignedContent?: {
    type: 'asset' | 'playlist' | 'schedule';
    _id: string;
    name: string;
  };
}

export const FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  WEB: 'web',
} as const;

export type FileType = (typeof FILE_TYPES)[keyof typeof FILE_TYPES];

export const ASSET_STATUS = {
  ACTIVE: 'active',
  CONVERTING: 'converting',
  FINISHED: 'finished',
  FAILED: 'failed',
} as const;

export type AssetStatus = (typeof ASSET_STATUS)[keyof typeof ASSET_STATUS];

// --- Value Objects ---

export interface AssetExpired {
  startDate?: string;
  endDate?: string;
  timezone?: string;
  scheduleId?: string;
  type?: string;
  is_delete?: boolean;
}

/** Input type for updateAssetExpires / deleteAssetExpires mutations */
export interface AssetExpiredInput {
  assetIds: string[];
  range?: {
    startDate?: string;
    endDate?: string;
  };
  timezone?: string;
  scheduleId?: string;
  type?: string;
  is_delete?: boolean;
}

export interface AssetOptions {
  [key: string]: unknown;
}

/**
 * Minimal asset shape shared between Asset and PlaylistAsset.
 * Used by getAssetDef() and AssetThumbnail to avoid unsafe double casts.
 * Both Asset and PlaylistAsset structurally satisfy this interface.
 */
export interface AssetLike {
  _id?: string;
  type?: string;
  fileType?: string;
  appType?: string;
  webType?: string;
  subType?: string;
  commonType?: string;
  serviceType?: string;
  youtubeType?: string;
  status?: string;
  thumbnail?: string;
  bucket?: string;
  AWSS3ID?: string;
  originalFileName?: string;
  assetExpired?: AssetExpired;
  isHide?: boolean;
  engage?: boolean;
}

// --- Main Asset Type ---

export interface Asset {
  _id: string;
  accountId?: string;
  teamId?: string;

  // Type classification
  type?: AssetType;
  fileType?: FileType;
  appType?: string;
  webType?: string;
  youtubeType?: string;
  subType?: string;
  commonType?: string;
  serviceType?: string;

  // File info
  originalFileName?: string;
  originalFileSize?: number;
  originalFileExtension?: string;
  fileSize?: number;
  fileExtension?: string;
  filename?: string;

  // Storage
  AWSS3ID?: string;
  originalAWSS3ID?: string;
  thumbnail?: string;
  bucket?: string;
  /** Transloadit assembly id of the current file. Needed by Replace Asset to set
   *  processIdUpdateFile (the upload-processor webhook restores it on assembly failure). */
  processId?: string;

  // Path / Group
  path?: string;
  groupId?: string;
  /** Parent app id when this asset is a child of a drive app (Google Drive / OneDrive /
   *  Dropbox). Present ⟹ read-only in the detail view (cannot rename/tag/delete). */
  assetRootId?: string;

  // Content
  webLink?: string;
  embedLink?: string;
  duration?: number;
  video_bitrate?: number;
  video_codec?: string;
  width?: number;
  height?: number;
  num_pages?: number;
  doc_pages?: string[];

  // Status
  status?: AssetStatus;
  contentStatus?: string;
  isDisable?: boolean;
  isHide?: boolean;

  // Emergency (Flash Message / CAP Alert)
  emergencyDuration?: number;
  currentDate?: string;
  startDate?: string;

  // Tags
  tags?: string[];

  // Engagement
  engage?: boolean;

  // Display name
  name?: string;

  // Cloud storage (OneDrive, Google Drive, Dropbox) — playlist backing this drive asset
  playlistId?: string;

  // SmallApp text content
  text?: string;

  // SmallApp post type discriminator (e.g. POSTS_TYPES.EDITOR, POSTS_TYPES.GOOGLE_TRAFIC)
  postsType?: string;

  // Options & metadata
  options?: AssetOptions;
  meta?: Record<string, unknown>;
  refreshInterval?: number;
  /** Website smallapp "Load only first time (no refresh)" toggle — pairs with refreshInterval: 0. */
  noRefresh?: boolean;

  // Web/app advanced fields
  javascriptMaxRetries?: number;
  requestDesktopSite?: boolean;
  isPasswordMaster?: boolean;
  newWebView?: boolean;
  isNormalWebview?: boolean;
  webPageUrl?: string;
  javascriptRun?: string;
  appVersion?: string;
  /** Default per-page playback duration (seconds) for PDF/document assets. */
  durationPage?: number;
  /** Sparse map of 0-based page index (string key) → seconds, for PDF/document
   *  per-page playback timing. Pages absent here fall back to `durationPage`. */
  pageDurations?: Record<string, number>;
  displayIniFrame?: boolean;
  isAutoScroll?: boolean;
  scrollType?: string;
  scrollTo?: string;
  pageHeight?: number;
  zoom?: number;
  preload?: boolean;

  // Google Traffic fields
  location?: Record<string, unknown>;
  isShowMarker?: boolean;

  // Google Sheets / Google Slides fields
  sheetList?: unknown[];
  speed?: number;
  speedValue?: number;
  versionApp?: string;
  scaleSpct?: number;
  horizontalAlignment?: string;
  verticalAlignment?: string;
  repeatInterval?: number;
  profile?: Record<string, unknown>;
  folder?: string;
  sheetSelected?: unknown;
  margins?: unknown;
  isMargins?: boolean;

  // YouTube variants (Playlist / Channel / Live Schedule)
  isShuffle?: boolean;
  /** ISO datetime — scheduled live-stream start time */
  liveAt?: string;
  /** 'none' | 'daily' | 'weekly' — Live Schedule recurrence */
  repeat?: string;

  // Web Scripting (scriptingAbility) — manifest-driven
  /** Plaintext recording JSON — write-only (BE encrypts to recordingJson) */
  recording?: string;
  /** Plaintext extra script — write-only (BE encrypts to scriptInjectionJson) */
  scriptInjection?: string;
  /** 2FA secret (encrypted on persist) */
  secretTwoFA?: string;
  /** 2FA recovery code (plaintext) */
  recordTwoFA?: string;
  /** Delay (seconds) before executing main login script */
  javascriptDelayExecute?: number;
  /** Delay (seconds) before executing 2FA script */
  javascriptDelay2FAExecute?: number;
  /** Encryption format marker — 'v2' for current scriptingAbility encryption */
  versionJson?: string;

  // Expiration
  assetExpired?: AssetExpired;

  // Dates
  createdAt?: string;
  lastUpdatedDate?: string;
  createdBy?: string;
  lastUpdatedBy?: string;

  // Twitter source criteria — player reads postsType + socialProfile|hashtag to know what to fetch
  socialProfile?: string;
  hashtag?: string;

  // Extended info (returned by detail queries)
  parentGroupInfo?: AssetGroup;
  createdByInfo?: {
    fullName?: string;
    userEmail?: string;
  };
  lastUpdatedByInfo?: {
    fullName?: string;
    userEmail?: string;
  };
}

// --- Connection / Pagination Types ---

export interface AssetEdge {
  cursor?: string;
  node: Asset;
}

export interface AssetPageInfo {
  startCursor?: string;
  endCursor?: string;
  hasPreviousPage?: boolean;
  hasNextPage: boolean;
}

export interface AssetPageData {
  limit?: number;
  offset?: number;
  total?: number;
}

export interface AssetGroup extends BaseFolderSecurity {
  name?: string;
  type?: GroupType;
  description?: string;
  thumbnail?: string;
  bucket?: string;
  createdAt?: string;
  lastUpdatedDate?: string;
  /** Populated user details for display in share confirmation */
  securitiesDetail?: Array<{ name: string; userId: string; role?: string }>;
  /** Populated team details for display in share confirmation */
  securityTeamsDetail?: Array<{ name: string; _id: string }>;
  sharedLink?: boolean;
  isFolderSecurityView?: boolean;
}

export interface AssetsResponse {
  page: {
    edges: AssetEdge[];
    pageInfo: AssetPageInfo;
  };
  pageData?: AssetPageData;
  extensions?: {
    groups?: AssetGroup[];
  };
}

// --- Input Types ---

export interface QueryAssetInput {
  _id?: string;
  _id_in?: string[];
  /** Exclude these ids — the "rest" section of the favorite-first split (client sends the fav id-set). */
  _id_nin?: string[];
  originalFileName?: string;
  originalFileName_contains?: string;
  originalFileName_startsWith?: string;
  originalFileName_regex?: string;
  isDisable?: boolean;
  type?: string;
  type_ne?: string;
  type_in?: string[];
  type_nin?: string[];
  appType?: string;
  appType_ne?: string | null;
  appType_in?: string[];
  appType_nin?: string[];
  subType?: string;
  subType_in?: string[];
  webType_in?: string[];
  commonType?: string;
  commonType_ne?: string | null;
  commonType_in?: string[];
  commonType_nin?: string[];
  fileType?: string;
  fileType_ne?: string;
  fileType_in?: string[];
  fileType_nin?: string[];
  originalFileExtension_nin?: string[];
  engage?: boolean;
  engage_ne?: boolean;
  engage_in?: boolean[];
  tags?: string;
  tags_in?: string[];
  path?: string | null;
  teamId?: string;
  /** When true, queries only assets visible via folder-level security sharing */
  isSharedWithMe?: boolean;
  /** Filter by root cloud storage asset ID (DRIVE mode — Google Drive, OneDrive, Dropbox) */
  assetRootId?: string;
  AND?: QueryAssetInput[];
  OR?: QueryAssetInput[];
}

export interface AssetSort {
  originalFileName?: SortDirection;
  createdAt?: SortDirection;
  lastUpdatedDate?: SortDirection;
}

export interface GroupSort {
  name?: SortDirection;
  createdAt?: SortDirection;
  lastUpdatedDate?: SortDirection;
}

export interface AssetInput {
  _id?: string;
  teamId?: string;
  type?: AssetType;
  fileType?: FileType;
  appType?: string;
  webType?: string;
  originalFileName?: string;
  originalFileSize?: number;
  path?: string;
  groupId?: string;
  tags?: string[];
  engage?: boolean;
  webLink?: string;
  thumbnail?: string;
  options?: AssetOptions;
  status?: AssetStatus;
  [key: string]: unknown;
}

export interface DeleteObjectInput {
  type?: string;
  typeGroup?: string;
  ids?: string[];
  groupIds?: string[];
}

export interface GroupInput {
  _id?: string;
  type?: GroupType;
  name?: string;
  description?: string;
  path?: string;
  teamId?: string;
  thumbnail?: string;
  bucket?: string;
  securities?: Record<string, unknown>[];
  securityTeams?: string[];
  isHoldDevice?: boolean;
  isFolderSecurityEdit?: boolean;
  isFolderSecurityView?: boolean;
}

export interface MoveToGroupInput {
  type?: GroupType;
  newPath?: string;
  ids?: string[];
  groupIds?: string[];
  toTeamId?: string;
  fromTeamId?: string;
}

// --- Asset Picker Types ---

export const ASSET_PICKER_TYPES = {
  DEFAULT: 'default',
  PLAYLIST: 'playlist',
  SPLIT_SCREEN: 'split-screen',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  AUDIO: 'audio',
  APP: 'app',
  AI: 'ai',
  SENSOR: 'sensor',
  FOLDER: 'folder',
  DESIGNER: 'designer',
  DESIGNER_LINK: 'designer-link',
  DESIGNER_CREATOR: 'designer-creator',
} as const;

export type AssetPickerType = (typeof ASSET_PICKER_TYPES)[keyof typeof ASSET_PICKER_TYPES];

/**
 * Client-side filter: returns true if the asset should be excluded from the picker.
 * Used to filter Atlas Search results which bypass server-side query filters.
 */
export function isAssetExcludedFromPicker(
  asset: { type?: string; fileType?: string; appType?: string; commonType?: string },
  pickerType: AssetPickerType = ASSET_PICKER_TYPES.DEFAULT,
): boolean {
  const filter = buildAssetPickerFilter(pickerType);
  const typeNin = filter.type_nin as string[] | undefined;
  const fileTypeNin = filter.fileType_nin as string[] | undefined;
  const appTypeNin = filter.appType_nin as string[] | undefined;
  const commonTypeNin = filter.commonType_nin as string[] | undefined;

  if (typeNin && asset.type && typeNin.includes(asset.type)) return true;
  if (fileTypeNin && asset.fileType && fileTypeNin.includes(asset.fileType)) return true;
  if (appTypeNin && asset.appType && appTypeNin.includes(asset.appType)) return true;
  if (commonTypeNin && asset.commonType && commonTypeNin.includes(asset.commonType)) return true;
  return false;
}

/** Build query filter for SelectAssetModal based on picker context.
 * NOTE: isAssetExcludedFromPicker() above derives its logic from this function.
 * If you change filter rules here, the client-side filter stays in sync automatically. */
export function buildAssetPickerFilter(
  pickerType: AssetPickerType = ASSET_PICKER_TYPES.DEFAULT,
): Partial<QueryAssetInput> {
  switch (pickerType) {
    case ASSET_PICKER_TYPES.DEFAULT:
      return {
        type_nin: [
          COMMON_TYPE.QR,
          APP_TYPES.EMERGENCY_MESSAGE,
          APP_TYPES.FLASH_MESSAGE,
          APP_TYPES.EMERGENCY_FEED,
          ASSET_TYPES.TEMPLATE,
          ASSET_TYPES.DATASOURCE,
        ],
        fileType_nin: [FILE_TYPES.AUDIO],
        commonType_nin: [...APP_COMMON_TYPES_REJECT_DEVICE],
        appType_nin: [...APP_REJECT_ASSIGN],
      };
    case ASSET_PICKER_TYPES.PLAYLIST:
      return {
        type_nin: [
          COMMON_TYPE.QR,
          APP_TYPES.EMERGENCY_MESSAGE,
          APP_TYPES.FLASH_MESSAGE,
          APP_TYPES.EMERGENCY_FEED,
          APP_TYPES.BACKGROUND_MUSIC,
          ASSET_TYPES.TEMPLATE,
          ASSET_TYPES.DATASOURCE,
          ASSET_TYPES.FOLDER,
        ],
        fileType_nin: [FILE_TYPES.AUDIO],
        appType_nin: [...APP_REJECT_ASSIGN, ...PLAYLIST_IGNORE_APP_TYPES],
        commonType_nin: [...APP_COMMON_TYPES_REJECT_DEVICE, COMMON_TYPE.LIVE_TV_ADS],
      };
    // Split-screen zone content picker — mirrors scio1
    // `splitScreenZonesModal.openPicker` (zonesModal.js:1128-1134):
    //   type ∉ {APP_COMMON_TYPES, splitScreen, template, dataSource}
    //   fileType ∉ {audio}
    //   commonType ∉ APP_COMMON_TYPES_REJECT_SPLITSCREEN
    //   appType ∉ APP_REJECT_ASSIGN
    case ASSET_PICKER_TYPES.SPLIT_SCREEN:
      return {
        type_nin: [ASSET_TYPES.SPLIT_SCREEN, ASSET_TYPES.TEMPLATE, ASSET_TYPES.DATASOURCE],
        fileType_nin: [FILE_TYPES.AUDIO],
        commonType_nin: [...APP_COMMON_TYPES_REJECT_SPLITSCREEN],
        appType_nin: [...APP_REJECT_ASSIGN],
      };
    case ASSET_PICKER_TYPES.IMAGE:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.IMAGE,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.VIDEO:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.VIDEO,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.DOCUMENT:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.DOCUMENT,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.AUDIO:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.AUDIO,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.APP:
      return {
        fileType: FILE_TYPES.WEB,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.AI:
      return {
        appType: APP_TYPES.AI,
      };
    case ASSET_PICKER_TYPES.SENSOR:
      return {
        appType: APP_TYPES.SENSOR,
      };
    case ASSET_PICKER_TYPES.FOLDER:
      return {};
    case ASSET_PICKER_TYPES.DESIGNER:
      // Backend handles filtering via extendParams.isFilterDesigner (appMeta tags: 'designer')
      return {};
    case ASSET_PICKER_TYPES.DESIGNER_LINK:
      // Backend handles filtering via extendParams.isFilterDesignerLink (appMeta tags: 'designer-link')
      return {};
    case ASSET_PICKER_TYPES.DESIGNER_CREATOR:
      // Opens Designer editor directly — no asset picker filter needed
      return {};
    default:
      return {};
  }
}

/**
 * Apply a user-selected fileType filter on top of a base query, removing
 * any conflicting `fileType_nin` so the backend doesn't receive both
 * `fileType: "document"` and `fileType.$nin` in the same query.
 */
export function applyFileTypeFilter<T extends Partial<QueryAssetInput>>(
  query: T,
  fileType: string | null,
): T {
  if (fileType) {
    query.fileType = fileType as T['fileType'];
    delete query.fileType_nin;
  }
  return query;
}
